import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import {
  Autocomplete,
  TextField,
  Stack,
  Box,
  Divider,
  Typography,
  Card,
  CardHeader,
  CardContent,
  IconButton,
  Alert,
  Fab,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined';
import AddIcon from '@mui/icons-material/Add';
import { ETransportTargetFreightPlutoDto, MinimalTarget } from '@/generated/model';
import { FreightDataChange } from '@/utilities/etransportTypes';
import { useTranslation } from 'react-i18next';
import { mapAvaliableTargetIDs, getPlateNumber } from './utils/utils';
import { DateTimePickerSelect } from '@/components/common/date/DateTimePickerSelect';

export function TargetFreightsSubmitForm({
  uit,
  minDate,
  maxDate,
  targets,
  setNewChanges,
  setOpenTargetFreightSubmitForm,
}: {
  uit: string | undefined;
  minDate?: Date;
  maxDate?: Date;
  targets: MinimalTarget[] | undefined;
  setNewChanges: Dispatch<SetStateAction<FreightDataChange | null>>;
  setOpenTargetFreightSubmitForm: Dispatch<SetStateAction<boolean>>;
}) {
  const { t } = useTranslation();
  const [currentValue, setCurrentValue] = useState<Date | null>(null);
  const [targetFreightFormData, setTargetFreightFormData] = useState<ETransportTargetFreightPlutoDto>({
    creationTimeMicros: undefined,
    startTimeMicros: undefined,
    endTimeMicros: undefined,
    targetId: undefined,
    uit: uit,
    trailer1: undefined,
    trailer2: undefined,
  });
  const isFormIncomplete: boolean = !targetFreightFormData.targetId || !targetFreightFormData.startTimeMicros;

  function handleSubmit(): void {
    setNewChanges((prev) => ({
      ...prev,
      uit: targetFreightFormData.uit,
      targetFreights: targetFreightFormData,
    }));
    handleClose();
  }

  function handleClose(): void {
    setOpenTargetFreightSubmitForm(false);
  }

  function handleDatePicker(date: Date) {
    if (!date) return;
    const validityStartTimeMicros = date.getTime() * 1000;
    setTargetFreightFormData({
      ...targetFreightFormData,
      startTimeMicros: validityStartTimeMicros,
      endTimeMicros: 0,
    });
    setCurrentValue(date);
  }

  const avaliableTargetIDs = useMemo(() => {
    return mapAvaliableTargetIDs(targets);
  }, [targets]);

  return (
    <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <Card
        elevation={3}
        sx={{
          flex: '1 1 auto',
          overflowY: 'overlay',
          padding: '5px',
          paddingBottom: '100px',
          marginTop: '5px',
          marginBottom: '10px',
          borderRadius: '5px',
          boxShadow: 6,
          minHeight: '98vh',
          maxHeight: '98vh',
        }}
      >
        <CardHeader
          title={
            <Typography variant="h5" sx={{ color: 'black' }}>
              {t('etransport.targetform.new')}
            </Typography>
          }
          action={
            <IconButton aria-label="settings">
              <CloseIcon onClick={handleClose} sx={{ color: 'black' }} />
            </IconButton>
          }
        />

        <Divider sx={{ my: 0.5 }} />

        <CardContent>
          <Stack spacing={3}>
            {isFormIncomplete && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {t('etransport.freightfrom.warning')}
              </Alert>
            )}
            <Autocomplete
              options={avaliableTargetIDs}
              getOptionLabel={(option) => {
                return getPlateNumber(targets, option) || 'Unkown';
              }}
              value={targetFreightFormData.targetId}
              onChange={(_, newValue) => {
                setTargetFreightFormData({ ...targetFreightFormData, targetId: newValue || undefined });
              }}
              sx={{ backgroundColor: 'var(--mui-palette-contrast-background)' }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('etransport.freightfrom.plate')}
                  placeholder={t('etransport.freightfrom.plateselect')}
                  required
                />
              )}
            />

            <DateTimePickerSelect
              sx={{ backgroundColor: 'var(--mui-palette-contrast-background)' }}
              label={t('etransport.freightfrom.validitystart') + ' *'}
              minDate={minDate}
              maxDate={maxDate}
              currentValue={currentValue}
              setSelectedDate={handleDatePicker}
            />

            <TextField
              label={t('etransport.freightfrom.trailer1')}
              value={targetFreightFormData.trailer1}
              onChange={(e) => {
                setTargetFreightFormData({ ...targetFreightFormData, trailer1: e.target.value });
              }}
              placeholder={t('etransport.freightfrom.platewrite')}
              fullWidth
              sx={{ backgroundColor: 'var(--mui-palette-contrast-background)' }}
            />

            <TextField
              label={t('etransport.freightfrom.trailer2')}
              value={targetFreightFormData.trailer2}
              onChange={(e) => {
                setTargetFreightFormData({ ...targetFreightFormData, trailer2: e.target.value });
              }}
              placeholder={t('etransport.freightfrom.platewrite')}
              fullWidth
              sx={{ backgroundColor: 'var(--mui-palette-contrast-background)' }}
            />
          </Stack>
        </CardContent>

        <Stack
          spacing={1}
          sx={{
            position: 'absolute',
            bottom: 20,
            right: 18,
          }}
        >
          <>
            <Fab
              variant="extended"
              onClick={handleSubmit}
              aria-label={t('etransport.freightfrom.save')}
              disabled={isFormIncomplete}
              sx={{
                boxShadow: 6,
                backgroundColor: 'var(--mui-palette-secondary-main)',
                color: 'white',
                border: '2px solid white',
                '&:hover': {
                  backgroundColor: 'var(--mui-palette-secondary-light)',
                },
              }}
            >
              <AddIcon sx={{ mr: 1 }} /> {t('etransport.targetform.add')}
            </Fab>
            <Fab
              variant="extended"
              onClick={handleClose}
              aria-label={t('etransport.freightfrom.cancel')}
              sx={{
                boxShadow: 6,
                backgroundColor: 'white',
                color: 'var(--mui-palette-secondary-main)',
                border: '2px solid var(--mui-palette-secondary-main)',
                '&:hover': {
                  backgroundColor: 'rgba(146, 102, 21, 0.1)',
                },
              }}
            >
              <HighlightOffOutlinedIcon sx={{ mr: 1 }} />
              {t('etransport.freightfrom.cancel')}
            </Fab>
          </>
        </Stack>
      </Card>
    </Box>
  );
}
